import logger from '../../utils/logger-rum';
/* eslint-disable no-param-reassign */
import { shouldEnableUserMedia } from '../utils';

export function getCameraPermissionState(forceEnabled = false): Promise<'denied' | 'prompt' | 'granted' | 'off'> {
	if (!shouldEnableUserMedia() && !forceEnabled) {
		return Promise.resolve('off');
	}
	return navigator.permissions?.query(
		{ name: ('camera'as PermissionName) },
	).then((result) => result.state);
}

export function getMicPermissionState(forceEnabled = false): Promise<'denied' | 'prompt' | 'granted' | 'off'> {
	if (!shouldEnableUserMedia() && !forceEnabled) {
		return Promise.resolve('off');
	}
	return navigator.permissions?.query(
		{ name: ('microphone' as PermissionName) },
	).then((result) => result.state);
}

export function getDefaultCameraStream(): Promise<MediaStream> {
	return navigator.mediaDevices.getUserMedia({
		video: {
			width: { ideal: 1920 },
			height: { ideal: 1080 },
		},
		audio: true,
	});
}

export function getCameraStream(
	videoId: string | undefined,
	audioId: string | undefined,
): Promise<MediaStream> {
	return navigator.mediaDevices.getUserMedia({
		video: videoId
			? { deviceId: { exact: videoId }, width: { ideal: 1920 }, height: { ideal: 1080 } }
			: false,
		audio: audioId
			? { deviceId: { exact: audioId } }
			: false,
	} as MediaStreamConstraints);
}

export function getCameraStreamNoAudio(videoId: string): Promise<MediaStream> {
	return navigator.mediaDevices.getUserMedia({
		video: {
			deviceId: { exact: videoId },
			width: { ideal: 1920 },
			height: { ideal: 1080 },
		},
		audio: false,
	});
}

export function deleteStream(stream: MediaStream | null): void {
	if (stream) {
		const tracks = stream.getTracks();
		tracks.forEach((track: MediaStreamTrack | null) => {
			if (track) track.stop();
			track = null;
		});
		stream = null;
	}
}

export function cleanupVideoStreamElement(videoEl: HTMLVideoElement | null) {
	// if the element is no longer in the DOM and the ref still has a srcObject,
	// we want to detach the srcObject to allow it to be garbage collected.
	if (videoEl) {
		videoEl.onpause = (e: Event) => {
			setTimeout(() => {
				if (!e.target && videoEl.srcObject) {
					videoEl.srcObject = null;
					videoEl.load();
					logger.log('video el srcObject detached', {});
				} else if (e.target && videoEl.srcObject) {
					videoEl.play().catch((err) => {
						logger.error('video el play request error', {
							location: 'cleanupVideoStreamElement',
						}, err);
					});
				}
			});
		};
	}
}

export async function getLiveDevices(
	vidId?: string,
	audId?: string,
): Promise<[boolean, boolean]> {
	if (!vidId && !audId) {
		return [false, false];
	}

	const str = await getCameraStream(vidId, audId);

	const videoTrack = str.getVideoTracks()[0];
	const audioTrack = str.getAudioTracks()[0];

	const hasCam = videoTrack?.readyState === 'live';
	const hasMic = audioTrack?.readyState === 'live';

	deleteStream(str);

	return [hasCam, hasMic];
}